import { defHttp } from '/@/utils/http/axios';

export interface Role {
  id: number;
  code: string;
  name: string;
  builtin: boolean;
  description?: string;
}

export interface Permission {
  id: number;
  code: string;
  module: string;
  name: string;
}

export interface UserRole {
  id: number;
  userId: number;
  roleId: number;
  scopeType: 'GLOBAL' | 'DATASET' | 'PROJECT';
  scopeId?: number;
}

export const listRoles = () => defHttp.get<Role[]>({ url: '/role/list' });

export const listAllPermissions = () =>
  defHttp.get<Permission[]>({ url: '/role/permissions' });

export const listRolePermissions = (roleId: number) =>
  defHttp.get<string[]>({ url: `/role/${roleId}/permissions` });

export const createRole = (params: { code: string; name: string; description?: string }) =>
  defHttp.post<Role>({ url: '/role/create', params });

export const setRolePermissions = (roleId: number, permissionIds: number[]) =>
  defHttp.post<null>({ url: `/role/${roleId}/permissions`, params: { permissionIds } });

export const listUserRoles = (userId: number) =>
  defHttp.get<UserRole[]>({ url: `/user-role/${userId}` });

export const grantRole = (params: {
  userId: number;
  roleId: number;
  scopeType?: UserRole['scopeType'];
  scopeId?: number;
}) => defHttp.post<null>({ url: '/user-role/grant', params });

export const revokeRole = (params: {
  userId: number;
  roleId: number;
  scopeType?: UserRole['scopeType'];
  scopeId?: number;
}) => defHttp.post<null>({ url: '/user-role/revoke', params });

export const adminChangePassword = (params: { userId: number; newPassword: string }) =>
  defHttp.post<null>({ url: '/user/admin/changePassword', params });

export const adminBatchChangePassword = (params: { userIds: number[]; newPassword: string }) =>
  defHttp.post<number>({ url: '/user/admin/batchChangePassword', params });

export interface RoleDraft {
  code: string;
  name: string;
  description?: string;
}

export interface BatchCreateRolesResult {
  created: Role[];
  skipped: string[];
}

export const batchCreateRoles = (items: RoleDraft[]) =>
  defHttp.post<BatchCreateRolesResult>({ url: '/role/batchCreate', params: { items } });

// ---------- admin dashboard --------------------------------------------------

export interface DashboardOverview {
  ongoingProjects: number;
  totalUsers: number;
  onlineUsers: number;
  /** 待终审帧数(已过质检,QC_PASSED) */
  pendingReview: number;
  /** 待质检帧数(SUBMITTED) */
  pendingQc: number;
  /** 未标注的包/独立数据数,管理员待办速览「未完成任务」用(老板 2026-09-28) */
  pendingAnnotation: number;
  /** 被驳回待返修的包/独立数据数,管理员待办速览用 */
  pendingRework: number;
  weeklyApprovalRate: number | null;
  weeklyVerdictCount: number;
}

// ---------- 人员统计总览(老板 2026-09-13) ----------------------------------

export interface AnnotatorStat {
  userId: number;
  name: string;
  boxCount: number;
  frameCount: number;
  approvedBoxCount?: number;
  /** 通过帧数(2026-09-20):该人首标且所在帧已终审入库的去重帧数 */
  approvedFrameCount?: number;
  modifiedBoxCount?: number;
  lastActiveAt: string | null;
}

export interface ReviewerStat {
  userId: number;
  name: string;
  qcPass: number;
  qcReject: number;
  qcTotal: number;
  auditPass: number;
  auditReject: number;
  auditTotal: number;
  lastActiveAt: string | null;
}

export interface StatsOverview {
  annotators: AnnotatorStat[];
  reviewers: ReviewerStat[];
}

export interface TrendPoint {
  date: string;
  /** 粒度(2026-09-20):DAY=按日 / WEEK=按周聚合(date 为该周周一) */
  unit?: 'DAY' | 'WEEK';
  boxCount: number;
  qcCount: number;
  auditCount: number;
}

export const getStatsOverview = () =>
  defHttp.get<StatsOverview>({ url: '/admin/dashboard/stats/overview' });

export const getStatsTrend = (days = 7) =>
  defHttp.get<TrendPoint[]>({ url: '/admin/dashboard/stats/trend', params: { days } });

export interface ProjectProgressRow {
  id: string;
  name: string;
  done: number;
  review: number;
  target: number;
  delta: string;
  deltaClass: 'up' | 'down' | 'flat';
  total: number;
}

export interface DataTypeSlice {
  type: string;
  name: string;
  frames: number;
  pct: number;
}

export interface ActivityRow {
  id: number;
  type: 'success' | 'warn' | 'danger' | 'info';
  action: string;
  userId?: number;
  userName: string;
  targetType?: string;
  targetId?: number;
  /** 目标项目标题(2026-09-20):优先显示,缺失回退 #id */
  targetName?: string | null;
  payload?: string;
  createdAt: string;
}

export interface RecentTaskRow {
  id: number;
  pid: string;
  title: string;
  datasetId: number;
  datasetName: string;
  datasetType?: 'IMAGE' | 'LIDAR_FUSION' | 'LIDAR_BASIC' | 'TEXT' | null;
  reviewStatus: 'NONE' | 'SUBMITTED' | 'QC_REJECTED' | 'QC_PASSED' | 'AUDIT_APPROVED';
  annotationStatus?: 'ANNOTATED' | 'NOT_ANNOTATED' | 'INVALID' | null;
  updatedAt: string;
  annotatorName?: string | null;
  reviewerName?: string | null;
  reviewStage?: 'QC' | 'AUDIT' | null;
  reviewVerdict?: 'PASS' | 'REJECT' | null;
}

/** 个人数据概览(老板 2026-09-29):质检/审核员主页激励卡数据源 */
export interface MyAnnotatorStat {
  userId: number;
  name: string;
  /** 人工标注框总数(不含模型预标注) */
  boxCount: number;
  /** 涉及帧数(去重) */
  frameCount: number;
  /** 涉及包数(帧按所属场景包去重) */
  packCount: number;
  approvedBoxCount: number;
  approvedFrameCount: number;
  /** 修改他人框的数量(返修贡献) */
  modifiedBoxCount: number;
}

export interface MyReviewerStat {
  userId: number;
  name: string;
  qcPass: number;
  qcReject: number;
  qcTotal: number;
  auditPass: number;
  auditReject: number;
  auditTotal: number;
  /** 质检经手量(老板 2026-09-30):包=裁定过的去重;帧=包内真实帧;框=帧内框总量 */
  qcPacks: number;
  qcFrames: number;
  qcBoxes: number;
  /** 终审经手量(同口径) */
  auditPacks: number;
  auditFrames: number;
  auditBoxes: number;
}

export interface MyStats {
  userId: number;
  annotator: MyAnnotatorStat | null;
  reviewer: MyReviewerStat | null;
  /** 本周轻排名(老板 2026-09-30):只显示在个人卡片,不出公开榜单 */
  weeklyBoxCount: number;
  weeklyBoxRank: number | null;
  weeklyBoxRankTotal: number;
  weeklyVerdictCount: number;
  weeklyVerdictRank: number | null;
  weeklyVerdictRankTotal: number;
}

export const getMyStats = () =>
  defHttp.get<MyStats>({ url: '/admin/dashboard/myStats' });

/**
 * 每日标注明细(老板 2026-09-30):每人每天 [人工首标, 模型预标注] 双口径矩阵,
 * 行尾附窗口内修正框量(修改非本人首标的框,含修模型框)。
 */
export interface DailyBoxRow {
  userId: number;
  name: string;
  /** 与 dates 对齐:每格 [人工首标, 模型预标注] */
  cells: [number, number][];
  humanTotal: number;
  modelTotal: number;
  fixTotal: number;
}

export interface DailyBoxes {
  /** 日期列(MM-dd,升序,含今天) */
  dates: string[];
  rows: DailyBoxRow[];
  /** 每日人工首标合计 */
  dailyTotals: number[];
}

export const getDailyBoxes = (days = 7) =>
  defHttp.get<DailyBoxes>({ url: '/admin/dashboard/stats/dailyBoxes', params: { days } });

export const getDashboardOverview = () =>
  defHttp.get<DashboardOverview>({ url: '/admin/dashboard/overview' });

export const getDashboardProjectProgress = (limit = 8) =>
  defHttp.get<ProjectProgressRow[]>({ url: '/admin/dashboard/projectProgress', params: { limit } });

export const getDashboardDataTypeDistribution = () =>
  defHttp.get<DataTypeSlice[]>({ url: '/admin/dashboard/dataTypeDistribution' });

export const getDashboardActivity = (limit = 10, userId?: number, action?: string) =>
  defHttp.get<ActivityRow[]>({ url: '/admin/dashboard/activity', params: { limit, userId, action } });

export interface ActivityFilters {
  users: { id: number; name: string }[];
  actions: string[];
}
export const getDashboardActivityFilters = () =>
  defHttp.get<ActivityFilters>({ url: '/admin/dashboard/activityFilters' });

export const getDashboardRecentTasks = (limit = 8, status?: RecentTaskRow['reviewStatus'] | 'REJECTED') =>
  defHttp.get<RecentTaskRow[]>({ url: '/admin/dashboard/recentTasks', params: { limit, status } });

export interface TopAnnotatorRow {
  userId: number;
  name: string;
  frames: number;
  saves: number;
  pct: number;
}

export const getDashboardTopAnnotators = (days = 1, limit = 5) =>
  defHttp.get<TopAnnotatorRow[]>({
    url: '/admin/dashboard/topAnnotators',
    params: { days, limit },
  });

export interface SparklinePoint { date: string; value: number }
export type SparklinesResponse = Record<
  'ongoing_projects' | 'total_users' | 'online_users' | 'pending_review',
  SparklinePoint[]
>;
export const getDashboardSparklines = (days = 14) =>
  defHttp.get<SparklinesResponse>({
    url: '/admin/dashboard/sparklines',
    params: { days },
  });

// ---------- admin dashboard review flow(任务状态流转可视化,2026-08-27) ----------

/** 审核流转五态,与后端 ReviewStatusEnum 对齐 */
export type ReviewFlowStatus = 'NONE' | 'SUBMITTED' | 'QC_PASSED' | 'QC_REJECTED' | 'AUDIT_APPROVED';

export interface ReviewFlowSlice {
  status: ReviewFlowStatus;
  count: number;
}

/**
 * GET /admin/dashboard/reviewFlow?datasetId=
 * 返回 data=[{status, count}],五值恒返回、按流转顺序;datasetId 缺省为全平台。
 * 后端未上线时接口报错,由页面兜底"暂不可用"文案 —— 不做假数据,
 * errorMessageMode 'none' 避免全局错误弹窗重复打扰。
 */
export const getDashboardReviewFlow = (datasetId?: number) =>
  defHttp.get<ReviewFlowSlice[]>(
    {
      url: '/admin/dashboard/reviewFlow',
      params: datasetId ? { datasetId } : undefined,
    },
    { errorMessageMode: 'none' },
  );

// ---------- global search (Cmd+K) -----------------------------------

export interface SearchHit {
  kind: 'dataset' | 'user' | 'data';
  id: number;
  title: string;
  subtitle?: string;
  hint?: string;
  link?: string;
}
export interface SearchResponse {
  datasets: SearchHit[];
  users: SearchHit[];
  data: SearchHit[];
}
export const globalSearch = (q: string) =>
  defHttp.get<SearchResponse>({ url: '/search', params: { q } });

// ---------- notifications -------------------------------------------

export interface NotificationRow {
  id: number;
  userId: number;
  type: string;
  title: string;
  body?: string;
  link?: string;
  readAt?: string;
  createdAt: string;
}
export interface NotificationListResponse {
  items: NotificationRow[];
  unread: number;
}
export const listNotifications = (limit = 20) =>
  defHttp.get<NotificationListResponse>({ url: '/notification/list', params: { limit } });
/** 按帧标注对象计数(GET /data/flow/annotateCounts):质检页"检查进度"分母 */
export const getAnnotateCounts = (dataIds: string) =>
  defHttp.get<Record<string, number>>({
    url: '/data/flow/annotateCounts',
    params: { dataIds },
    headers: {
      // @ts-ignore
      ignoreCancelToken: true,
    },
  });

export const getUnreadCount = () =>
  defHttp.get<{ unread: number }>({ url: '/notification/unreadCount' });
export const markNotificationRead = (id: number) =>
  defHttp.post<null>({ url: `/notification/${id}/read` });
export const markAllNotificationsRead = () =>
  defHttp.post<{ updated: number }>({ url: '/notification/markAllRead' });

// ---------- review (QA / audit) -----------------------------------------------

export interface ReviewRecord {
  id: number;
  dataId: number;
  reviewerId: number;
  stage: 'QC' | 'AUDIT';
  verdict: 'PASS' | 'REJECT';
  comment?: string;
  createdAt: string;
  flaggedObjectIds?: number[];
}

export const getReviewRecords = (dataId: number) =>
  defHttp.get<ReviewRecord[]>({ url: `/data/flow/reviewRecords/${dataId}` });

export interface ReviewErrorType {
  id: number;
  name: string;
  enabled: boolean;
  sortOrder: number;
}
export const getReviewErrorTypes = () =>
  defHttp.get<ReviewErrorType[]>({ url: '/data/review/errorTypes' });
export const createReviewErrorType = (name: string) =>
  defHttp.post<ReviewErrorType>({ url: '/data/review/errorTypes', data: { name } });
export const updateReviewErrorType = (id: number, body: { name?: string; enabled?: boolean }) =>
  defHttp.put<ReviewErrorType>({ url: `/data/review/errorTypes/${id}`, data: body });

export const submitQc = (
  dataId: number,
  body: { verdict: 'PASS' | 'REJECT'; comment?: string; flaggedObjectIds?: number[]; reworkDueAt?: string },
) => defHttp.post<null>({ url: `/data/flow/qc/submit/${dataId}`, params: body });

export const submitAudit = (
  dataId: number,
  body: { verdict: 'PASS' | 'REJECT'; comment?: string; reworkDueAt?: string },
) => defHttp.post<null>({ url: `/data/flow/audit/submit/${dataId}`, params: body });

/**
 * 驳回弹窗预检(老板 2026-09-30):originalDueAt=原截止(最近标注派单截止,
 * 无派单回落数据集截止);overdue=true 表示已过原截止或从未设截止——
 * 此时前端必须强制弹窗让质检/审核填写返修截止才能提交驳回。
 */
export interface ReworkDueInfo {
  originalDueAt: string | null;
  overdue: boolean;
}

export const getReworkDueInfo = (dataId: number) =>
  defHttp.get<ReworkDueInfo>({ url: `/data/flow/reworkDueInfo/${dataId}` });

export interface MyRejectedRow {
  dataId: number;
  dataName: string;
  datasetId: number;
  /** 数据集类型(老板 2026-09-07):重新标注按类型跳 pc/image 工具 */
  datasetType?: string;
  /** 数据自身类型(老板 2026-09-11):SCENE=场景包行,重新标注须整包取号 */
  dataType?: string;
  /** 驳回类型:QC = 质检驳回(改完重提即可)/ AUDIT = 终审驳回(整体重做) */
  stage?: 'QC' | 'AUDIT';
  rejectedAt?: string;
  rejectComment?: string;
  reviewerId?: number;
  /** 返修截止(老板 2026-09-30):驳回时确定——未过原截止沿用原截止,过截止由质检/审核填写 */
  reworkDueAt?: string;
}

export const getMyRejected = () =>
  defHttp.get<MyRejectedRow[]>({ url: '/data/flow/myRejected' });

/** 质检员待办队列行(老板 2026-09-04):全部已提交待质检的数据 */
export interface QcQueueRow {
  dataId: number;
  dataName: string;
  datasetId: number;
  datasetName?: string;
  annotatorName?: string;
  submittedAt?: string;
  /** 审核占用(老板 2026-09-30):他人持有时前端置灰"XX 审核中" */
  reviewHolderId?: number | null;
  reviewHolderName?: string | null;
  /** 截止时间(老板 2026-09-30):归属行的派单截止 */
  dueAt?: string | null;
}

/**
 * 审核占用锁(老板 2026-09-30):acquired=true 可审;false 时
 * holderName 为当前占用人,前端横幅提示并禁用裁定。
 */
export interface ReviewLockResult {
  acquired: boolean;
  holderId?: number;
  holderName?: string | null;
  heldAt?: string;
}

export const acquireReviewLock = (dataId: number) =>
  defHttp.post<ReviewLockResult>({ url: `/data/flow/reviewLock/acquire/${dataId}` });

export const releaseReviewLock = (dataId: number) =>
  defHttp.post<null>({ url: `/data/flow/reviewLock/release/${dataId}` });

/** 我领取的审核任务行(老板 2026-09-30) */
export interface ClaimedReviewRow {
  dataId: number;
  dataName: string;
  datasetId: number;
  datasetName?: string | null;
  reviewStatus?: string | null;
  heldAt?: string | null;
}

export const getMyClaimed = () =>
  defHttp.get<ClaimedReviewRow[]>({ url: '/data/flow/myClaimed' });

/** 当前账号的质检/终审裁定历史；记录的裁定与数据当前流转状态分别展示。 */
export interface MyReviewHistoryRow {
  reviewRecordId: number;
  dataId: number;
  dataName?: string | null;
  datasetId?: number | null;
  datasetName?: string | null;
  stage: 'QC' | 'AUDIT';
  verdict: 'PASS' | 'REJECT';
  /** null 表示整包裁定，非空数组表示本次裁定的子帧。 */
  frameIds?: number[] | null;
  flaggedObjectIds?: number[] | null;
  comment?: string | null;
  createdAt?: string | null;
  currentReviewStatus?: string | null;
}

export interface MyReviewHistoryPage {
  records: MyReviewHistoryRow[];
  total: number;
}

export const getMyReviewHistory = (params: {
  verdict?: 'PASS' | 'REJECT';
  pageNo: number;
  pageSize: number;
}) => defHttp.get<MyReviewHistoryPage>({ url: '/data/flow/myReviewHistory', params });

export const getQcQueue = () =>
  defHttp.get<QcQueueRow[]>({ url: '/data/flow/qcQueue' });

/** IAA 双标抽检(开发计划 1.3):发起抽检(影子帧+派单) */
export function iaaCreate(datasetId: number, dataIds: number[], assigneeId: number) {
  return defHttp.post<any>({ url: '/data/flow/iaa/create', data: { datasetId, dataIds, assigneeId } });
}

/** IAA 双标抽检:结果与汇总(质量看板) */
export function iaaResults(pageNo = 1, pageSize = 10) {
  return defHttp.get<any>({ url: '/data/flow/iaa/results', params: { pageNo, pageSize } });
}

/** 自动质检违规项(P0-1):提交时规则引擎产出,审核页警告面板 */
export function getQualityIssues(sceneId: number | string) {
  return defHttp.get<any[]>({ url: `/data/flow/qualityIssues/${sceneId}` });
}

/** 终审员待办队列(老板 2026-09-05):全部质检通过待终审的数据 */
export const getAuditQueue = () =>
  defHttp.get<QcQueueRow[]>({ url: '/data/flow/auditQueue' });

// ---------- admin user creation & account status (P0 批量建号 / P1 单建 / 停用启用) -----
// 后端接口契约(2026-08-26 拍板):后端未上线时调用会报错,由页面兜底提示"后端服务即将就绪"。
// errorMessageMode 'none':错误由调用方按场景友好化展示,避免全局 toast 复述一次性密码/重复报错。

/** 四种内置角色(与后端 RoleEnum / spec §2.1 一致) */
export type AdminRoleCode = 'ANNOTATOR' | 'QC' | 'AUDITOR' | 'ADMIN';

export interface BatchCreateUserItem {
  nickname: string;
  email: string;
  role: AdminRoleCode;
}

/** 批量建号逐行结果:成功行含一次性 14 位初始密码,失败行含中文原因 */
export interface BatchCreateUserResultRow {
  nickname: string;
  email: string;
  role: string;
  /** 仅 success=true 时返回(随机 14 位),只在本接口响应中出现,不落前端日志 */
  password?: string;
  success: boolean;
  /** 失败原因,如"邮箱已存在"/"邮箱格式错误"/"文件内重复" */
  reason?: string;
}

/** POST /user/admin/batchCreate body {users:[...]} → data 为逐行结果数组 */
export const adminBatchCreateUsers = (users: BatchCreateUserItem[]) =>
  defHttp.post<BatchCreateUserResultRow[]>(
    { url: '/user/admin/batchCreate', params: { users } },
    { errorMessageMode: 'none' },
  );

/** POST /user/admin/create body {nickname,email,role} → data {password} 或业务错误 */
export const adminCreateUser = (params: {
  nickname: string;
  email: string;
  role: AdminRoleCode;
}) =>
  defHttp.post<{ password: string }>({ url: '/user/admin/create', params }, { errorMessageMode: 'none' });

/** POST /user/admin/changeStatus body {userId,disabled} → 成功无数据 */
export const adminChangeUserStatus = (params: { userId: number; disabled: boolean }) =>
  defHttp.post<null>({ url: '/user/admin/changeStatus', params }, { errorMessageMode: 'none' });
